import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import rbacService from "../../services/rbacService";
import { queryKeys } from "./queryKeys";
import { store } from "../../store";
import { fetchMyPermissions } from "../../store/slices/permissionsSlice";

// Default/Global Roles
export const useDefaultRoles = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.roles(), "default"],
    queryFn: async () => {
      const res = await rbacService.getDefaultRoles();
      return res.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - default roles don't change often
  });
};

export const useCreateDefaultRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleData) => rbacService.createDefaultRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.roles(), "default"],
      });
    },
  });
};

export const useUpdateDefaultRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roleData }) =>
      rbacService.updateDefaultRole(id, roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.roles(), "default"],
      });
    },
  });
};

export const useDeleteDefaultRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => rbacService.deleteDefaultRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.roles(), "default"],
      });
    },
  });
};

export const useDefaultPermissionsMatrix = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.permissions(), "default", "matrix"],
    queryFn: () => rbacService.getDefaultPermissionsMatrix(),
    staleTime: 10 * 60 * 1000,
  });
};

export const useUpdateDefaultPermissionsMatrix = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (matrix) =>
      rbacService.updateDefaultPermissionsMatrix(matrix),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.permissions(), "default"],
      });
    },
  });
};

// Institute-specific Roles
export const useInstituteRoles = (instituteId, filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.rbac.roles(), "institute", instituteId, filters],
    queryFn: () => rbacService.getInstituteRoles(instituteId, filters),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useInstituteRole = (instituteId, roleId) => {
  return useQuery({
    queryKey: [
      ...queryKeys.rbac.roles(),
      "institute",
      instituteId,
      "detail",
      roleId,
    ],
    queryFn: () => rbacService.getInstituteRoleById(instituteId, roleId),
    enabled: !!instituteId && !!roleId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateInstituteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ instituteId, roleData }) =>
      rbacService.createInstituteRole(instituteId, roleData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...queryKeys.rbac.roles(),
          "institute",
          variables.instituteId,
        ],
      });
    },
  });
};

export const useUpdateInstituteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ instituteId, roleId, roleData }) =>
      rbacService.updateInstituteRole(instituteId, roleId, roleData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...queryKeys.rbac.roles(),
          "institute",
          variables.instituteId,
        ],
      });
    },
  });
};

export const useDeleteInstituteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ instituteId, roleId }) =>
      rbacService.deleteInstituteRole(instituteId, roleId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...queryKeys.rbac.roles(),
          "institute",
          variables.instituteId,
        ],
      });
    },
  });
};

export const useInstituteRolePermissions = (instituteId, roleId) => {
  return useQuery({
    queryKey: queryKeys.rbac.rolePermissions(roleId),
    queryFn: () => rbacService.getInstituteRolePermissions(instituteId, roleId),
    enabled: !!instituteId && !!roleId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssignInstituteRolePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ instituteId, roleId, permissions }) =>
      rbacService.assignInstituteRolePermissions(
        instituteId,
        roleId,
        permissions
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.rolePermissions(variables.roleId),
      });
      try { store.dispatch(fetchMyPermissions()); } catch (e) {}
    },
  });
};

export const useResetInstituteRolesToDefaults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instituteId) =>
      rbacService.resetInstituteRolesToDefaults(instituteId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.roles(), "institute", variables],
      });
    },
  });
};

export const useInitializeInstituteRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instituteId) =>
      rbacService.initializeInstituteRoles(instituteId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.roles(), "institute", variables],
      });
    },
  });
};

// Legacy/General endpoints
export const useRoles = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.rbac.roles(),
    queryFn: async () => {
      const res = await rbacService.getRoles(filters);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Returns roles formatted for dropdown/select components.
 * Use in Add User, filters, etc. so RBAC-created roles appear everywhere.
 */
export const useRolesForSelect = () => {
  const { data: rolesRes, ...rest } = useRoles();
  const rolesFromApi = Array.isArray(rolesRes?.data) ? rolesRes.data : (Array.isArray(rolesRes) ? rolesRes : []);
  const roles = rolesFromApi.map((r) => ({
    id: r.slug || r.role,
    label: r.name || (r.slug === "superAdmin" ? "Super Admin" : r.slug === "hod" ? "HOD" : (r.slug || "").charAt(0).toUpperCase() + (r.slug || "").slice(1)),
  }));
  return { roles, ...rest };
};

export const useRole = (id) => {
  return useQuery({
    queryKey: [...queryKeys.rbac.roles(), "detail", id],
    queryFn: () => rbacService.getRoleById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleData) => rbacService.createRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.roles(),
      });
      // A new role adds a column to the permission matrix; refresh it too.
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.permissions(), "matrix"],
      });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roleData }) => rbacService.updateRole(id, roleData),
    onSuccess: (res, variables) => {
      const body = res?.data ?? {};
      const rolePayload = body?.data ?? {};
      const newId = rolePayload?.id;
      // Defer cache refresh so Save UI can close immediately after the PUT returns.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.rbac.roles(), "detail", variables.id],
        });
        if (newId != null && String(newId) !== String(variables.id)) {
          queryClient.invalidateQueries({
            queryKey: [...queryKeys.rbac.roles(), "detail", newId],
          });
        }
        queryClient.invalidateQueries({
          queryKey: queryKeys.rbac.roles(),
        });
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.rbac.permissions(), "matrix"],
        });
        try {
          store.dispatch(fetchMyPermissions());
        } catch (e) {}
      }, 0);
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => rbacService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.roles(),
      });
    },
  });
};

export const usePermissions = () => {
  return useQuery({
    queryKey: queryKeys.rbac.permissions(),
    queryFn: () => rbacService.getPermissions(),
    staleTime: 30 * 60 * 1000, // 30 minutes - permissions don't change often
  });
};

export const useRolePermissions = (roleId) => {
  return useQuery({
    queryKey: queryKeys.rbac.rolePermissions(roleId),
    queryFn: () => rbacService.getRolePermissions(roleId),
    enabled: !!roleId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssignPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, permissions }) =>
      rbacService.assignPermissions(roleId, permissions),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.rolePermissions(variables.roleId),
      });
    },
  });
};

export const useFeatures = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.all, "features"],
    queryFn: () => rbacService.getFeatures(),
    staleTime: 30 * 60 * 1000,
  });
};

export const useRoleFeatures = (roleId) => {
  return useQuery({
    queryKey: [...queryKeys.rbac.all, "features", roleId],
    queryFn: () => rbacService.getRoleFeatures(roleId),
    enabled: !!roleId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssignFeatures = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, features }) =>
      rbacService.assignFeatures(roleId, features),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.all, "features", variables.roleId],
      });
    },
  });
};

export const useRoleHierarchy = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.all, "hierarchy"],
    queryFn: () => rbacService.getRoleHierarchy(),
    staleTime: 10 * 60 * 1000,
  });
};

export const useUpdateRoleHierarchy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hierarchy) => rbacService.updateRoleHierarchy(hierarchy),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.all, "hierarchy"],
      });
    },
  });
};

// ==========================================
// NEW PERMISSION MANAGEMENT HOOKS
// ==========================================

export const useAllPermissions = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.permissions(), "all"],
    queryFn: async () => {
      const res = await rbacService.getAllPermissions();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const usePermissionsForRole = (targetRole) => {
  return useQuery({
    queryKey: [...queryKeys.rbac.permissions(), "role", targetRole],
    queryFn: async () => {
      const res = await rbacService.getPermissionsForRole(targetRole);
      return res.data;
    },
    enabled: !!targetRole,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssignPermissionsToRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, permissions }) =>
      rbacService.assignPermissionsToRole(roleId, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.permissions(),
      });
      try { store.dispatch(fetchMyPermissions()); } catch (e) {}
    },
  });
};

export const useSeedDefaultPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => rbacService.seedDefaultPermissions(payload || { replace: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.permissions(),
      });
      try { store.dispatch(fetchMyPermissions()); } catch (e) {}
    },
  });
};

export const useAllRolesWithStats = (options = {}) => {
  const { includeInactive = true } = options;
  return useQuery({
    queryKey: [...queryKeys.rbac.roles(), "with-stats", includeInactive ? "all" : "active"],
    queryFn: async () => {
      const res = await rbacService.getAllRolesWithStats(
        includeInactive ? { includeInactive: true } : undefined
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const usePermissionMatrix = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.permissions(), "matrix"],
    queryFn: async () => {
      const res = await rbacService.getPermissionMatrix();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useBulkUpdatePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates) => rbacService.bulkUpdatePermissions(updates),
    onSuccess: async () => {
      // Force an IMMEDIATE network refetch of the permission matrix (not just a
      // stale-mark). invalidateQueries alone can leave the on-screen matrix showing
      // the pre-save checkbox state until the 5-min staleTime elapses or the query
      // is re-observed, which is why a saved uncheck appeared to "not remove" in the
      // UI. refetchQueries re-runs the query function now and awaits it so the
      // rebuilt-from-server checkboxes reflect the DB. Prefix key matches the
      // "matrix", "all", "with-stats" and role permission child queries.
      try {
        await queryClient.refetchQueries({
          queryKey: queryKeys.rbac.permissions(),
          type: "active",
        });
      } catch (e) {
        // Fall back to invalidation if a forced refetch fails for any reason.
        queryClient.invalidateQueries({ queryKey: queryKeys.rbac.permissions() });
      }
      try { store.dispatch(fetchMyPermissions()); } catch (e) {}
    },
  });
};

export const useAllModules = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.all, "modules"],
    queryFn: async () => {
      const res = await rbacService.getAllModules();
      return res.data;
    },
    staleTime: 0, // No caching for modules since they can be created dynamically
  });
};

export const useCreateModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (moduleData) => rbacService.createModule(moduleData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.rbac.all, "modules"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.permissions(),
      });
      // Also refetch the modules query explicitly
      queryClient.refetchQueries({
        queryKey: [...queryKeys.rbac.all, "modules"],
      });
      try { store.dispatch(fetchMyPermissions()); } catch (e) {}
    },
  });
};

export const useExportPermissions = () => {
  return useQuery({
    queryKey: [...queryKeys.rbac.permissions(), "export"],
    queryFn: async () => {
      const res = await rbacService.exportPermissions();
      return res.data;
    },
    enabled: false,
  });
};

export const useImportPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permissionsData) => rbacService.importPermissions(permissionsData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rbac.permissions(),
      });
      try { store.dispatch(fetchMyPermissions()); } catch (e) {}
    },
  });
};

